import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import pmgLogo from "./pmg.jpeg";
import "./AdminInvoiceGenerator.scss";

const DEFAULT_ITEMS = [
  { id: "item-1", no: "No. 01", description: "RSVP Component", total: "4500" },
  { id: "item-2", no: "No. 02", description: "Liquor Component", total: "FREE" },
  {
    id: "item-3",
    no: "No. 03",
    description: "Digital Invitation & Wedding Web Site",
    total: "FREE",
  },
  { id: "item-4", no: "No. 04", description: "Seating Arrangements", total: "1500" },
  { id: "item-5", no: "No. 05", description: "Budget Management", total: "2000" },
  { id: "item-6", no: "No. 06", description: "QR Code", total: "500" },
  {
    id: "item-7",
    no: "No. 07",
    description: "Guest Adding By Plan My Guest Team",
    total: "1000",
  },
];

const formatDateForInvoice = (value) => {
  if (!value) return "";

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

const getTodayForInvoice = () => formatDateForInvoice(new Date());

const getMobileNumber = (user) =>
  user?.mobile_number ||
  user?.contact_number ||
  user?.mobile ||
  user?.phone ||
  user?.phoneNumber ||
  user?.mobileNumber ||
  "";

const sanitizeFileName = (value) =>
  String(value || "invoice")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const createInvoiceRef = (selectedUser) => {
  const rawValue =
    selectedUser?.invoiceRef ||
    selectedUser?.id ||
    selectedUser?.slug ||
    "0000";

  const digits = String(rawValue).replace(/\D/g, "");
  if (digits.length >= 4) return `PMG-${digits.slice(-4)}`;

  const fallback = String(rawValue)
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(-4)
    .toUpperCase();

  return `PMG-${fallback || "0000"}`;
};

const toNumber = (value) => {
  const cleaned = String(value || "")
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "");

  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
};

const AdminInvoiceGenerator = ({ selectedUser, isPremium }) => {
  const invoiceRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [items, setItems] = useState(DEFAULT_ITEMS);

  const [invoiceData, setInvoiceData] = useState({
    coupleNames: "",
    contactNo: "",
    expiryDate: "",
    paymentDate: getTodayForInvoice(),
    weddingUrl: "",
    refNo: "",
    subtotal: "9500",
    discount: "3510",
  });

  useEffect(() => {
    if (!selectedUser) return;

    setItems(DEFAULT_ITEMS);

    setInvoiceData({
      coupleNames: `${selectedUser.bride_name || "Bride"} & ${
        selectedUser.groom_name || "Groom"
      }`,
      contactNo: getMobileNumber(selectedUser),
      expiryDate: formatDateForInvoice(
        selectedUser.customExpiryDate ||
          selectedUser.expiry_date ||
          selectedUser.wedding_date
      ),
      paymentDate: getTodayForInvoice(),
      weddingUrl: selectedUser.slug
        ? `www.planmyguest.com/sites/${selectedUser.slug}`
        : "www.planmyguest.com",
      refNo: createInvoiceRef(selectedUser),
      subtotal: "9500",
      discount: "3510",
    });
  }, [selectedUser?.id]);

  const finalTotal = useMemo(() => {
    const subtotal = toNumber(invoiceData.subtotal);
    const discount = toNumber(invoiceData.discount);
    return Math.max(subtotal - discount, 0);
  }, [invoiceData.subtotal, invoiceData.discount]);

  const fileName = useMemo(() => {
    const brideName = selectedUser?.bride_name || "Bride";
    const groomName = selectedUser?.groom_name || "Groom";
    return `${sanitizeFileName(`${brideName} & ${groomName}`)}.pdf`;
  }, [selectedUser?.bride_name, selectedUser?.groom_name]);

  const updateInvoiceField = (key, value) => {
    setInvoiceData((prev) => ({ ...prev, [key]: value }));
  };

  const updateItem = (id, key, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: `item-${Date.now()}`,
        no: `No. ${String(prev.length + 1).padStart(2, "0")}`,
        description: "New Invoice Item",
        total: "0",
      },
    ]);
  };

  const deleteItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const downloadPdf = async () => {
    if (!invoiceRef.current) return;

    try {
      setIsDownloading(true);

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2.3,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        removeContainer: true,
      });

      const imageData = canvas.toDataURL("image/jpeg", 0.92);

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pdfWidth,
        pdfHeight,
        undefined,
        "MEDIUM"
      );

      pdf.save(fileName);
    } catch (error) {
      console.error("Failed to download invoice:", error);
      alert("Failed to download invoice. Please check console.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!selectedUser) return null;

  return (
    <>
      <ButtonComponent
        className="adminInvoiceGenerator__openBtn"
        onClick={() => setIsOpen(true)}
        disabled={!isPremium}
        title={isPremium ? "Generate invoice" : "Invoice is only for premium users"}
      >
        Generate Invoice
      </ButtonComponent>

      {isOpen && (
        <div
          className="adminInvoiceGenerator__overlay"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="adminInvoiceGenerator__modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="adminInvoiceGenerator__modalHeader">
              <div>
                <p className="adminInvoiceGenerator__eyebrow">Invoice</p>
                <h2>Generate Invoice</h2>
              </div>

              <button
                type="button"
                className="adminInvoiceGenerator__close"
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="adminInvoiceGenerator__modalBody">
              <aside className="adminInvoiceGenerator__editor">
                <div className="adminInvoiceGenerator__editorSection">
                  <h3>Customer Details</h3>

                  <div className="adminInvoiceGenerator__editorGrid">
                    <label>
                      Couple Names
                      <input
                        value={invoiceData.coupleNames}
                        onChange={(e) =>
                          updateInvoiceField("coupleNames", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Contact No
                      <input
                        value={invoiceData.contactNo}
                        onChange={(e) =>
                          updateInvoiceField("contactNo", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Expiry Date
                      <input
                        value={invoiceData.expiryDate}
                        onChange={(e) =>
                          updateInvoiceField("expiryDate", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Payment Date
                      <input
                        value={invoiceData.paymentDate}
                        onChange={(e) =>
                          updateInvoiceField("paymentDate", e.target.value)
                        }
                      />
                    </label>

                    <label className="adminInvoiceGenerator__wideField">
                      Wedding URL
                      <input
                        value={invoiceData.weddingUrl}
                        onChange={(e) =>
                          updateInvoiceField("weddingUrl", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Ref No
                      <input
                        value={invoiceData.refNo}
                        onChange={(e) =>
                          updateInvoiceField("refNo", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Subtotal
                      <input
                        value={invoiceData.subtotal}
                        onChange={(e) =>
                          updateInvoiceField("subtotal", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Discount
                      <input
                        value={invoiceData.discount}
                        onChange={(e) =>
                          updateInvoiceField("discount", e.target.value)
                        }
                      />
                    </label>

                    <label>
                      Total
                      <input value={finalTotal} readOnly />
                    </label>
                  </div>
                </div>

                <div className="adminInvoiceGenerator__itemsHeader">
                  <h3>Invoice Items</h3>
                  <ButtonComponent onClick={addItem}>Add Item</ButtonComponent>
                </div>

                <div className="adminInvoiceGenerator__itemsEditor">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="adminInvoiceGenerator__itemEditorRow"
                    >
                      <input
                        className="adminInvoiceGenerator__itemNoInput"
                        value={item.no}
                        onChange={(e) =>
                          updateItem(item.id, "no", e.target.value)
                        }
                      />

                      <input
                        className="adminInvoiceGenerator__itemDescInput"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                      />

                      <input
                        className="adminInvoiceGenerator__itemTotalInput"
                        value={item.total}
                        onChange={(e) =>
                          updateItem(item.id, "total", e.target.value)
                        }
                      />

                      <button
                        type="button"
                        className="adminInvoiceGenerator__deleteItemBtn"
                        onClick={() => deleteItem(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </aside>

              <div className="adminInvoiceGenerator__previewWrap">
                <div className="adminInvoiceGenerator__invoice" ref={invoiceRef}>
                  <div className="adminInvoiceGenerator__topStrips">
                    <span></span>
                    <span></span>
                  </div>

                  <div className="adminInvoiceGenerator__invoiceInner">
                    <div className="adminInvoiceGenerator__brandRow">
                      <div>
                        <h1>PLAN MY GUEST</h1>
                        <h2>070 536 7980</h2>
                        <p>planmyguestbookings@gmail.com</p>
                      </div>

                      <div className="adminInvoiceGenerator__logoBox">
                        <img src={pmgLogo} alt="Plan My Guest" />
                      </div>
                    </div>

                    <div className="adminInvoiceGenerator__metaGrid">
                      <p>
                        Couple Names: <strong>{invoiceData.coupleNames}</strong>
                      </p>
                      <p>
                        Contact No: <strong>{invoiceData.contactNo}</strong>
                      </p>
                      <p>
                        Expiry Date: <strong>{invoiceData.expiryDate}</strong>
                      </p>
                      <p>
                        Payment Date: <strong>{invoiceData.paymentDate}</strong>
                      </p>
                    </div>

                    <p className="adminInvoiceGenerator__urlLine">
                      Wedding URL:{" "}
                      <strong>
                        <u>{invoiceData.weddingUrl}</u>
                      </strong>
                    </p>

                    <p className="adminInvoiceGenerator__refLine">
                      Ref No: <strong>{invoiceData.refNo}</strong>
                    </p>

                    <div className="adminInvoiceGenerator__table">
                      <div className="adminInvoiceGenerator__tableHeader">
                        <span>No.</span>
                        <span>Description</span>
                        <span>Total (LKR)</span>
                      </div>

                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="adminInvoiceGenerator__tableRow"
                        >
                          <span>{item.no}</span>
                          <span>{item.description}</span>
                          <span>{item.total}</span>
                        </div>
                      ))}

                      <div className="adminInvoiceGenerator__tableRow adminInvoiceGenerator__tableRow--empty">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>

                      <div className="adminInvoiceGenerator__tableRow adminInvoiceGenerator__tableRow--empty">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>

                    <div className="adminInvoiceGenerator__paidStamp">PAID</div>

                    <div className="adminInvoiceGenerator__bottomArea">
                      <p>
                        Thank you for choosing
                        <br />
                        PlanMyGuest to manage your
                        <br />
                        special day
                      </p>

                      <div className="adminInvoiceGenerator__totals">
                        <div>
                          <strong>SUBTOTAL</strong>
                          <span>{invoiceData.subtotal}</span>
                        </div>
                        <div>
                          <strong>DISCOUNT</strong>
                          <span>{invoiceData.discount}</span>
                        </div>
                        <div>
                          <strong>TOTAL (LKR)</strong>
                          <span>{finalTotal}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="adminInvoiceGenerator__footer">
                    <span>WWW.PLANMYGUEST.COM</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="adminInvoiceGenerator__modalActions">
              <ButtonComponent onClick={downloadPdf} disabled={isDownloading}>
                {isDownloading ? "Preparing PDF..." : "Download PDF"}
              </ButtonComponent>

              <ButtonComponent onClick={() => setIsOpen(false)}>
                Close
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminInvoiceGenerator;